// i18n: translated
/**
 * ローディングスピナー
 *
 * AceCollect ロゴの周りをリングが滑らかに回転。
 * ロゴは静止、リングのみ回転。
 *
 * PR-52 (#290): theme-aware 化。
 *   - ロゴは light = 素の色 (黒+ブランド赤) / dark = invert で白
 *     (旧実装の常時 invert は light で白抜き沈みを起こしていた)
 *   - 背景リングの base color を light / dark で出し分け
 *   - fullScreen overlay の背景も theme-aware
 *   - 回転は animate-spin。prefers-reduced-motion 時は完全停止ではなく低速回転
 *     (SPINNER_RING_SPIN_CLASS、freeze と区別がつくよう動きは維持する)
 */
import logoIconUrl from '../../assets/logos/logo-icon.svg?url';
import { useT } from '@/i18n';
import { commonNs } from '@/i18n/namespaces/common';
import {
  SPINNER_SIZE_CONFIG,
  resolveSpinnerLogoClass,
  SPINNER_RING_BASE_CLASS,
  SPINNER_RING_SPIN_CLASS,
  SPINNER_FULLSCREEN_OVERLAY_CLASS,
  SPINNER_INLINE_WRAPPER_CLASS,
} from '@/utils/loadingSpinner';

interface LoadingSpinnerProps {
  fullScreen?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export function LoadingSpinner({ fullScreen = false, size = 'md' }: LoadingSpinnerProps) {
  const t = useT(commonNs);
  const cfg = SPINNER_SIZE_CONFIG[size];
  const half = cfg.ring / 2;
  const r = half - cfg.stroke - 1;
  const circ = 2 * Math.PI * r;

  const spinner = (
    <div className={`relative ${cfg.outer}`} role="status" aria-label={t('status.loading')}>
      {/* 背景リング (静止) — theme-aware base color */}
      <svg className="absolute inset-0 h-full w-full" viewBox={`0 0 ${cfg.ring} ${cfg.ring}`}>
        <circle
          cx={half}
          cy={half}
          r={r}
          fill="none"
          stroke="currentColor"
          strokeWidth={cfg.stroke}
          className={SPINNER_RING_BASE_CLASS}
        />
      </svg>

      {/* 回転リング — 通常 1s 回転 / reduced-motion 時は低速回転 (完全停止しない) */}
      <svg
        className={`absolute inset-0 h-full w-full ${SPINNER_RING_SPIN_CLASS}`}
        viewBox={`0 0 ${cfg.ring} ${cfg.ring}`}
      >
        <circle
          cx={half}
          cy={half}
          r={r}
          fill="none"
          stroke="url(#spinGradient)"
          strokeWidth={cfg.stroke}
          strokeLinecap="round"
          strokeDasharray={`${circ * 0.3} ${circ * 0.7}`}
        />
        <defs>
          <linearGradient id="spinGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#6366f1" />
            <stop offset="100%" stopColor="#a5b4fc" stopOpacity="0.3" />
          </linearGradient>
        </defs>
      </svg>

      {/* AceCollect ブランドロゴ (静止) — light: 素の色 / dark: invert で白 */}
      <div className="absolute inset-0 flex items-center justify-center">
        <img
          src={logoIconUrl}
          alt=""
          aria-hidden="true"
          className={resolveSpinnerLogoClass(cfg.logo)}
        />
      </div>
    </div>
  );

  if (fullScreen) {
    return (
      <div
        className={`fixed inset-0 z-40 flex items-center justify-center ${SPINNER_FULLSCREEN_OVERLAY_CLASS}`}
      >
        {spinner}
      </div>
    );
  }

  return <div className={SPINNER_INLINE_WRAPPER_CLASS}>{spinner}</div>;
}
